/**
 * @file     types.js - export consistent non-dcp types for use within DCP libraries.
 *
 *           There is a very frustrating problem with how Node.js resolves non-relative module
 *           identifiers; if a module is in a directory that is different from the program's directory
 *           the module's rules for resolving the modules apply, and if a third module can be resolved
 *           via package.json/node_modules differently, it will prefer that path.
 *
 *           Concretely, a program in /home/wes/git/dcp/ormdb that calls require('bignumber.js') will
 *           get a *different module* than the require('bignumber.js') inside the database schema code,
 *           because that code always runs from /var/dcp.  The first one loads
 *           /home/wes/git/dcp/node_modules/bignumber.js, but the schema loads
 *           /var/dcp/node_modules/bignumber.js. This has the effect of not only wasting memory, but
 *           breaking instanceof BigNumber checks!
 *
 *           The solution is to always load types from the same place.  This doesn't necessarily apply
 *           to types loaded via require('dcp/....') because rtlink wallpapers over this problem, but
 *           it definitely applies to BigNumber.
 *
 *           Of course, that means that *this* module always needs to be loaded from a consistent
 *           location, also.  dcp-rtlink and dcp-client's "dcp/" module space should handle that
 *           transparently for us: always require('dcp/utils'), never require('/path/utils').
 *
 * @author   Wes Garland, wes@distributive.network
 * @date     July 2025
 */
'use strict';

var lazyExport = require;
if (typeof __DCP_CLIENT__ === 'undefined')
  lazyExport = s => s;

Object.defineProperty(exports, 'BigNumber',           pd(lazyExport('bignumber.js'),          'BigNumber'));
Object.defineProperty(exports, 'Address',             pd(lazyExport('dcp/dcp-client/wallet'), 'Address'));
Object.defineProperty(exports, 'Keystore',            pd(lazyExport('dcp/dcp-client/wallet'), 'Keystore'));
Object.defineProperty(exports, 'IdKeystore',          pd(lazyExport('dcp/dcp-client/wallet'), 'IdKeystore'));
Object.defineProperty(exports, 'BankAccountKeystore', pd(lazyExport('dcp/dcp-client/wallet'), 'BankAccountKeystore'));
Object.defineProperty(exports, 'DcpURL',              pd(lazyExport('dcp/common/dcp-url'),    'DcpURL'));

/**
 * Make a property descriptor for a lazily-defined export. This keeps us from loading unreferenced
 * modules in Node.js. In Webpack, lazy-loading has no meaning and trying to lazy load breaks Webpack's
 * ability to parse the module to discover it's exports (thanks James) so we just pass the whole module
 * to this function instead.
 */
function pd(module, symbolName)
{
  return {
    configurable: true,
    get () {
      delete exports[symbolName];
      if (typeof module === 'string')
        module = require(module);
      exports[symbolName] = module[symbolName];
      return exports[symbolName];
    }
  };
  return module[symbolName];
}
